import { useState } from "react";
import { Shield } from "lucide-react";
import { S } from "@/lib/strings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface ConsentGateProps {
  onConfirm: () => Promise<void>;
  onDecline: () => void;
  isNewUser?: boolean;
}

export function ConsentGate({ onConfirm, onDecline, isNewUser = false }: ConsentGateProps) {
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    if (!accepted) return;
    setLoading(true);
    setError("");
    try {
      await onConfirm();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">{S.CONSENT_TITLE}</CardTitle>
          <CardDescription>{S.CONSENT_SUBTITLE}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-3">
            <Checkbox
              id="consent"
              checked={accepted}
              onCheckedChange={(v) => setAccepted(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
              {S.CONSENT_CHECKBOX}{" "}
              <a
                href="https://fintective.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="https://fintective.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary"
              >
                Privacy Policy
              </a>
              .
            </Label>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button
            className="w-full"
            disabled={!accepted || loading}
            onClick={handleConfirm}
          >
            {loading ? "..." : S.CONSENT_CONFIRM}
          </Button>

          <button
            type="button"
            onClick={onDecline}
            className="w-full text-center text-sm text-muted-foreground hover:text-primary hover:underline"
          >
            {isNewUser ? S.CONSENT_DECLINE : S.CONSENT_DECLINE_EXISTING}
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
